import { z } from 'zod';

/** Required string that rejects whitespace-only values */
const requiredString = z.string().trim().min(1);

const optionalString = z
  .string()
  .optional()
  .transform((v) => v?.trim() || undefined);
const checkbox = z
  .literal('on')
  .optional()
  .transform((v) => v === 'on' || undefined);

/** Transforms empty string to undefined for select fields */
const optionalEnum = <T extends [string, ...string[]]>(values: T) =>
  z
    .enum(values)
    .or(z.literal(''))
    .optional()
    .nullable()
    .transform((v) => (v === '' ? undefined : v) as (typeof values)[number] | undefined);

export const applicationSchema = z.object({
  salutation: z.enum(['mr', 'ms', 'none']),
  title: optionalString,
  firstName: requiredString,
  lastName: requiredString,
  email: requiredString.email(),
  phone: optionalString,
  street: optionalString,
  streetNumber: optionalString,
  additional: optionalString,
  countryCode: optionalString,
  zipCode: optionalString,
  city: optionalString,
  birthday: z
    .string()
    .optional()
    .transform((v) => v?.trim() || undefined)
    .refine((v) => !v || v <= new Date().toISOString().split('T')[0], {
      message: 'birthday-future',
    }),
  birthplace: optionalString,
  fieldOfStudy: optionalString,
  lastAnnualSalary: optionalString,
  lastAnnualSalaryCurrency: optionalString,
  desiredSalary: z.coerce.number().optional(),
  earliestPossibleJoinDate: optionalString,
  hasNoticePeriod: checkbox,
  noticePeriodTimeAmount: z.coerce.number().optional(),
  noticePeriodTimeUnit: optionalEnum(['days', 'weeks', 'months']),
  noticePeriodTimeReference: optionalEnum([
    'endOfMonth',
    'endOfQuarter',
    'endOfYear',
    'fifteenthOfMonth',
    'fifteenthOrEndOfMonth',
    'onEveryDay',
  ]),
  isSeverelyDisabled: checkbox,
  howDidYouHearAboutUs: z.string().trim().default(''),
  remark: optionalString,
  permissionToStoreApplication: checkbox,
  mayBePassedWithinCorporateGroup: checkbox,
  // hasPrivacyTermsAccepted hat kein Formularfeld – wird in sendApplication fest auf true gesetzt.
});

/** Maps visibleFields keys to form field names that should be required when Mandatory */
const mandatoryFieldMap: Record<string, string[]> = {
  mobilePhone: ['phone'],
  address: ['street', 'streetNumber', 'zipCode', 'city', 'countryCode'],
  birthday: ['birthday'],
  birthPlace: ['birthplace'],
  fieldOfStudy: ['fieldOfStudy'],
  lastAnnualSalary: ['lastAnnualSalary'],
  desiredSalary: ['desiredSalary'],
  earliestPossibleJoinDate: ['earliestPossibleJoinDate'],
  noticePeriod: ['noticePeriodTimeAmount', 'noticePeriodTimeUnit', 'noticePeriodTimeReference'],
  remark: ['remark'],
};

/** Builds a dynamic schema that makes fields required based on visibleFields config */
export const buildApplicationSchema = (visibleFields?: Record<string, string> | null) => {
  if (!visibleFields) return applicationSchema;

  const overrides: Record<string, z.ZodTypeAny> = {};
  const noticePeriodVisible =
    visibleFields.noticePeriod &&
    visibleFields.noticePeriod !== 'NotVisible' &&
    visibleFields.noticePeriod !== 'Administrator';

  for (const [settingsKey, formKeys] of Object.entries(mandatoryFieldMap)) {
    if (visibleFields[settingsKey] === 'Mandatory') {
      for (const key of formKeys) {
        // noticePeriod sub-fields handled conditionally below
        if (settingsKey === 'noticePeriod') continue;
        // birthday needs min(1) for mandatory + refine for future date
        if (key === 'birthday') {
          overrides[key] = requiredString.refine((v) => v <= new Date().toISOString().split('T')[0], {
            message: 'birthday-future',
          });
          continue;
        }
        overrides[key] = key === 'desiredSalary' ? z.coerce.number().min(1) : requiredString;
      }
    }
  }

  const baseSchema = Object.keys(overrides).length > 0 ? applicationSchema.extend(overrides) : applicationSchema;

  // Conditional validation: noticePeriod fields required when checkbox is checked
  // This applies whenever the notice period section is visible (user can opt-in)
  if (noticePeriodVisible) {
    return baseSchema.superRefine((data, ctx) => {
      if (data.hasNoticePeriod !== true) return;

      if (!data.noticePeriodTimeAmount || data.noticePeriodTimeAmount <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'number',
          inclusive: true,
          path: ['noticePeriodTimeAmount'],
        });
      }
      if (!data.noticePeriodTimeUnit) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'string',
          received: 'undefined',
          path: ['noticePeriodTimeUnit'],
        });
      }
      if (!data.noticePeriodTimeReference) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'string',
          received: 'undefined',
          path: ['noticePeriodTimeReference'],
        });
      }
      // Max 2 years validation
      const maxByUnit: Record<string, number> = { days: 730, weeks: 104, months: 24 };
      const max = data.noticePeriodTimeUnit ? maxByUnit[data.noticePeriodTimeUnit] || 730 : 730;
      if (data.noticePeriodTimeAmount && data.noticePeriodTimeAmount > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          maximum: max,
          type: 'number',
          inclusive: true,
          path: ['noticePeriodTimeAmount'],
          message: 'notice-period-max',
        });
      }
    });
  }

  return baseSchema;
};

export type ApplicationFormData = z.infer<typeof applicationSchema>;
